/**
 * Session detail tab (participants).
 */
import * as React from 'react';
import type { LiveKitRoomPresenceStatus, Session, SessionTab } from '@/types/remote/session.types';
import {
  SESSION_PARTICIPANT_ROLE_LABELS,
  LIVEKIT_ROOM_STATUS_CONFIG,
  LIVEKIT_BUSINESS_ROLE_ANIMATOR_CONFIG,
} from '@/components/sessions/session-configs';
import { LabelBadge } from '@/components/common/LabelBadge';
import { Clock, CheckCircle2, XCircle } from 'lucide-react';
import { resolveLucideIcon } from '@/hooks/useRoleConfig';

/** Props for {@link SessionParticipantsTab}. */
interface SessionParticipantsTabProps {
  session: Session;
  activeTab?: SessionTab;
  presenceResponse: { roomStatus?: string; participants?: any[] } | undefined;
  presenceLoading: boolean;
}

/** Session detail tab (participants). */
export const SessionParticipantsTab: React.FC<SessionParticipantsTabProps> = ({
  session,
  activeTab,
  presenceResponse,
  presenceLoading,
}) => (
  <div className="space-y-4">
    <div className="flex items-center justify-between">
      <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
        Présence en temps réel
        {presenceLoading && (
          <span className="text-xs font-normal text-gray-400">Mise à jour...</span>
        )}
      </h4>
      {presenceResponse?.roomStatus &&
        (() => {
          const roomCfg =
            LIVEKIT_ROOM_STATUS_CONFIG[presenceResponse.roomStatus as LiveKitRoomPresenceStatus];
          if (!roomCfg) return null;
          return (
            <LabelBadge
              label={roomCfg.label}
              icon={roomCfg.icon}
              variant={roomCfg.badgeVariant}
              color={roomCfg.badgeColor}
              size="sm"
            />
          );
        })()}
    </div>

    {(session.status === 'IN_PROGRESS' || session.actualStartTime) && (
      <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
        <h5 className="text-xs font-semibold text-green-800 mb-2 flex items-center gap-2">
          <Clock className="w-3.5 h-3.5" />
          Timing
        </h5>
        <dl className="grid grid-cols-3 gap-2 text-xs">
          <div>
            <dt className="text-green-700">Début effectif</dt>
            <dd className="font-medium">{session.actualStartTime ?? session.startTime}</dd>
          </div>
          <div>
            <dt className="text-green-700">Fin prévue</dt>
            <dd className="font-medium">{session.endTime ?? 'N/A'}</dd>
          </div>
        </dl>
      </div>
    )}

    {presenceResponse?.participants && presenceResponse.participants.length > 0 ? (
      <div className="space-y-3">
        {presenceResponse.participants.map((presence) => (
          <div key={presence.userId} className="p-3 border rounded-lg">
            <div className="flex justify-between items-start mb-3">
              <div className="flex items-center gap-2">
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium flex-shrink-0"
                  style={{ backgroundColor: 'var(--color-1-nuance-1)' }}
                >
                  {presence.displayName?.[0] ?? 'P'}
                </div>
                <div>
                  <div className="font-medium text-sm">{presence.displayName}</div>
                  <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                    {(() => {
                      const label =
                        presence.roleLabel ??
                        SESSION_PARTICIPANT_ROLE_LABELS[presence.role] ??
                        presence.role;
                      let color = 'var(--color-1-nuance-2)';
                      if (presence.roleColor) {
                        color = presence.roleColor.startsWith('color-')
                          ? `var(--${presence.roleColor})`
                          : presence.roleColor;
                      }
                      return (
                        <LabelBadge
                          label={label}
                          icon={
                            presence.roleIcon ? resolveLucideIcon(presence.roleIcon) : undefined
                          }
                          variant="ghost"
                          color={color}
                          size="sm"
                        />
                      );
                    })()}
                    {presence.businessRole === 'animator' && (
                      <LabelBadge
                        label={LIVEKIT_BUSINESS_ROLE_ANIMATOR_CONFIG.label}
                        icon={LIVEKIT_BUSINESS_ROLE_ANIMATOR_CONFIG.icon}
                        variant={LIVEKIT_BUSINESS_ROLE_ANIMATOR_CONFIG.badgeVariant}
                        color={LIVEKIT_BUSINESS_ROLE_ANIMATOR_CONFIG.badgeColor}
                        size="sm"
                      />
                    )}
                  </div>
                </div>
              </div>
              <LabelBadge
                label={presence.isPresent ? 'Présent' : 'Absent'}
                icon={presence.isPresent ? CheckCircle2 : XCircle}
                variant="outlined"
                color={
                  presence.isPresent ? 'rgb(var(--status-success))' : 'rgb(var(--color-error))'
                }
                size="sm"
              />
            </div>

            <dl className="grid grid-cols-2 gap-2 text-xs">
              <div>
                <dt className="text-gray-500">1ère connexion</dt>
                <dd className="font-medium">
                  {presence.firstJoinedAt
                    ? new Date(presence.firstJoinedAt).toLocaleTimeString('fr-FR', {
                        hour: '2-digit',
                        minute: '2-digit',
                      })
                    : '—'}
                </dd>
              </div>
              <div>
                <dt className="text-gray-500">Dernière déconnexion</dt>
                <dd className="font-medium">
                  {presence.lastLeftAt
                    ? new Date(presence.lastLeftAt).toLocaleTimeString('fr-FR', {
                        hour: '2-digit',
                        minute: '2-digit',
                      })
                    : '—'}
                </dd>
              </div>
              <div>
                <dt className="text-gray-500">Durée totale</dt>
                <dd className="font-medium">
                  {presence.totalDurationSeconds
                    ? `${Math.round(presence.totalDurationSeconds / 60)} min`
                    : '—'}
                </dd>
              </div>
              <div>
                <dt className="text-gray-500">Connexions</dt>
                <dd className="font-medium">{presence.connectionCount ?? 0}</dd>
              </div>
            </dl>
          </div>
        ))}
      </div>
    ) : (
      <p className="text-sm text-gray-500">
        Aucune donnée de présence disponible
        {session.status !== 'IN_PROGRESS' && session.status !== 'COMPLETED' && (
          <span className="block mt-1 text-xs">(La séance n'a pas encore commencé)</span>
        )}
      </p>
    )}

    {activeTab === 'information-meeting' && (session.maxStudents ?? 0) > 0 && (
      <div className="p-3 bg-gray-50 rounded">
        <p className="text-sm text-gray-700">
          <strong>Capacité :</strong> {presenceResponse?.participants?.length ?? 0} /{' '}
          {session.maxStudents} participants
        </p>
      </div>
    )}
  </div>
);
